import { LiveAttributionWindow, LIVE_ATTRIBUTION_WINDOW_LABELS } from '../types'

/**
 * Os filtros com que um resultado foi calculado.
 *
 * O painel mostra o resultado da ÚLTIMA execução até a próxima. Sem guardar
 * com que filtros ele saiu, trocar o período ou a janela deixava a tela igual —
 * e quem trocou achava que o número já era do filtro novo. Comparar estes
 * parâmetros com os filtros atuais é o que permite dizer "isto está
 * desatualizado" (e o quê mudou).
 */
export interface ProcessParams {
  mode: 'stories' | 'live'
  selection: 'company' | 'group'
  companyKey: string | null
  companyTitle: string | null
  /** Chaves das empresas do grupo, ordenadas (o grupo é o resultado dos filtros). */
  groupKeys: string[] | null
  startDate: string
  endDate: string
  /** Só no modo live; a janela não afeta stories. */
  window: LiveAttributionWindow | null
}

export type ProcessParamChange = 'empresa' | 'grupo de empresas' | 'período' | 'janela de atribuição'

/**
 * O que mudou entre os filtros do resultado mostrado e os atuais. Vazio =
 * o resultado na tela corresponde aos filtros. `shown` nulo (nada processado)
 * também é vazio: não há resultado para ficar desatualizado.
 */
export function describeParamChanges(shown: ProcessParams | null, current: ProcessParams): ProcessParamChange[] {
  if (!shown) return []
  const changes: ProcessParamChange[] = []
  if (current.selection === 'company') {
    if (shown.companyKey !== current.companyKey) changes.push('empresa')
  } else if ((shown.groupKeys ?? []).join('|') !== (current.groupKeys ?? []).join('|')) {
    changes.push('grupo de empresas')
  }
  if (shown.startDate !== current.startDate || shown.endDate !== current.endDate) changes.push('período')
  if (current.mode === 'live' && shown.window !== current.window) changes.push('janela de atribuição')
  return changes
}

/** "o período e a janela de atribuição" — para a frase do aviso. */
export function joinChanges(changes: ProcessParamChange[]): string {
  const withArticle = changes.map((c) => (c === 'empresa' || c === 'janela de atribuição' ? `a ${c}` : `o ${c}`))
  if (withArticle.length <= 1) return withArticle.join('')
  return `${withArticle.slice(0, -1).join(', ')} e ${withArticle[withArticle.length - 1]}`
}

/** "Meu Rodapé · 01/10/2026 a 31/10/2026 · janela 4 horas" */
export function describeParams(p: ProcessParams): string {
  const who = p.selection === 'company'
    ? p.companyTitle ?? p.companyKey ?? 'empresa'
    : `grupo de ${p.groupKeys?.length ?? 0} empresas`
  const parts = [who, `${formatDay(p.startDate)} a ${formatDay(p.endDate)}`]
  if (p.mode === 'live' && p.window) parts.push(`janela ${LIVE_ATTRIBUTION_WINDOW_LABELS[p.window]}`)
  return parts.join(' · ')
}

function formatDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}
